import React, { useState, useEffect, useRef } from 'react'
import { Input, Typography, List, Grid } from 'antd'
import { useTranslation } from 'react-i18next'
import updateSession from '../../utils/session/updateSession'
import { FileTextOutlined } from '@ant-design/icons'

const { Text } = Typography
const { useBreakpoint } = Grid

const MAX_NOTES = 10

export default function RecordingNotesSection({ sessionUuid }) {
  const { t } = useTranslation()
  const screens = useBreakpoint()

  const [notes, setNotes] = useState([])
  const [currentNote, setCurrentNote] = useState('')
  const [editingIndex, setEditingIndex] = useState(null)
  const [error, setError] = useState('')
  const [isContainerFocused, setIsContainerFocused] = useState(false)

  const notesContainerRef = useRef(null)
  const mainInputRef = useRef(null)

  const updateUserNotes = async (updatedNotes) => {
    const markdownNotes = updatedNotes.length
      ? updatedNotes.map((n) => `- ${n}`).join('\n')
      : ''

    try {
      await updateSession(sessionUuid, { user_notes: markdownNotes });
    } catch (err) {
      console.error('Failed to update session notes:', err)
    }
  }

  const resetZoomOnMobile = () => {
    if (!screens.md) {
      document.activeElement?.blur()
      window.scrollTo(0, 0)
    }
  }

  const handleAddNote = async (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const trimmed = currentNote.trim()
      if (notes.length >= MAX_NOTES) {
        setError(t('session_recorder_notepad.errors.max_notes_reached'))
        return
      }
      if (trimmed) {
        const updated = [...notes, trimmed]
        setNotes(updated)
        setCurrentNote('')
        setError('')
        await updateUserNotes(updated)
        resetZoomOnMobile()
      }
    }
  }

  const handleMainKeyDown = async (e) => {
    if ((e.key === 'Backspace' || e.key === 'Delete') && currentNote.trim() === '' && notes.length > 0) {
      e.preventDefault()
      setEditingIndex(notes.length - 1)
    }
  }

  const handleEditNoteEnter = async (e, index) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const updated = e.target.value.trim()
      if (updated) {
        const updatedNotes = notes.map((note, i) => (i === index ? updated : note))
        setNotes(updatedNotes)
        setEditingIndex(null)
        await updateUserNotes(updatedNotes)
      } else {
        await handleDeleteNote(index, true)
      }
      mainInputRef.current?.focus()
    }
  }

  const handleEditKeyDown = async (e, index) => {
    if ((e.key === 'Backspace' || e.key === 'Delete') && e.target.value.trim() === '') {
      e.preventDefault()
      await handleDeleteNote(index, true)
    }
  }

  const handleDeleteNote = async (index, moveToPrevious = false) => {
    const updated = notes.filter((_, i) => i !== index)
    setNotes(updated)
    if (moveToPrevious && index > 0) setEditingIndex(index - 1)
    else setEditingIndex(null)
    setError('')
    await updateUserNotes(updated)
  }

  useEffect(() => {
    if (notesContainerRef.current) {
      notesContainerRef.current.scrollTop = notesContainerRef.current.scrollHeight
    }
  }, [notes])

  return (
    <div style={{marginTop: 40, textAlign: 'left', width: '100%'}}>
      <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: 6}}>
        <FileTextOutlined style={{marginRight: 8, marginTop: 4}}/>
        <div style={{display: 'flex', flexDirection: 'column'}}>
          <Text>{t('recording_notes_section.title')}</Text>
        </div>
      </div>
      <div
        style={{
          border: isContainerFocused ? '3px solid black' : '1px solid #d9d9d9',
          borderRadius: 8,
          padding: 5,
          minHeight: 30,
          marginTop: 5,
          marginBottom: 10,
          textAlign: 'left'
        }}
      >
        <div ref={notesContainerRef} style={{maxHeight: 100, overflowY: 'auto'}}>
          {notes.length > 0 && (
            <List
              dataSource={notes}
              split={false}
              renderItem={(note, index) => (
                <List.Item key={index} style={{padding: '2px 0'}}>
                  {editingIndex === index ? (
                    <Input
                      autoFocus
                      defaultValue={note}
                      onBlur={() => {
                        setEditingIndex(null)
                        setIsContainerFocused(false)
                      }}
                      onFocus={() => setIsContainerFocused(true)}
                      onKeyPress={(e) => handleEditNoteEnter(e, index)}
                      onKeyDown={(e) => handleEditKeyDown(e, index)}
                      style={{width: '100%', border: 'none', boxShadow: 'none'}}
                    />
                  ) : (
                    <span
                      onClick={() => setEditingIndex(index)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: '#f0fdf4',
                        padding: '2px 6px',
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    >
                      {note}
                    </span>
                  )}
                </List.Item>
              )}
            />
          )}
        </div>
        <Input.TextArea
          placeholder={
            !isContainerFocused && (notes.length === 0) ? t('recording_notes_section.placeholder') : ''
          }
          autoSize={{ minRows: 1, maxRows: 3 }}
          value={currentNote}
          onChange={(e) => setCurrentNote(e.target.value)}
          onKeyPress={handleAddNote}
          onKeyDown={handleMainKeyDown}
          ref={mainInputRef}
          onFocus={() => setIsContainerFocused(true)}
          onBlur={() => setIsContainerFocused(false)}
          style={{
            fontSize: 16,
            border: 'none',
            boxShadow: 'none',
            resize: 'none',
            marginTop: notes.length > 0 ? 2 : 0,
          }}
        />

        {isContainerFocused && (
          <Text
            type="secondary"
            style={{
              display: 'block',
              fontSize: 12,
              marginTop: 6,
              textAlign: 'right',
            }}
          >
            {t('session_recorder_notepad.submit_hint')}
          </Text>
        )}
        {error && (
          <Text type="danger" style={{display: 'block', marginTop: 8, fontSize: 12}}>
            {error}
          </Text>
        )}
      </div>
    </div>
  )
}